import { exportIpeLayersFromAllSlides, isIpeLayerName, normalizeIpeLayerName } from '@/image-designer/adapters/ipeLayerAdapter';
import {
  enrichSlideHierarchyFields,
  groupSlidesByMain,
} from '@/image-designer/utils/templateImageHierarchy';
import { toPrintSlotRef } from './imageAssetModel';

/**
 * 解析套图模板元数据（页数、ipeLayer 贴合位）
 */
export function summarizePodTemplate(template) {
  if (!template) {
    return { slideCount: 0, slotCount: 0, slotNames: [], pages: [] };
  }

  const td = template.templateData || {};
  const slideCanvases = td.slideCanvases || {};
  const images = Array.isArray(td.images) ? td.images : [];
  const slideIds = Array.isArray(td.slideCanvasIds) && td.slideCanvasIds.length > 0
    ? td.slideCanvasIds.map(String)
    : Object.keys(slideCanvases);
  const slideCount = slideIds.length > 0
    ? slideIds.length
    : (td.fabricCanvas ? 1 : Math.max(images.length, 1));

  let ipeLayers = td.ipeLayers || {};
  if (!Object.keys(ipeLayers).length && slideIds.length > 0) {
    ipeLayers = exportIpeLayersFromAllSlides(slideCanvases);
  }

  const slotNameSet = new Set();
  Object.values(ipeLayers).forEach((layers) => {
    (layers || []).forEach((layer) => {
      if (layer?.name) slotNameSet.add(layer.name);
    });
  });

  const stampAreas = td.stampAreas || template.stampAreas || {};
  if (slotNameSet.size === 0) {
    Object.values(stampAreas).forEach((areas) => {
      (areas || []).forEach((area) => {
        if (area?.name) slotNameSet.add(area.name);
      });
    });
  }

  const slotNames = [...slotNameSet].sort((a, b) => {
    const ai = /^ipelayer(\d*)$/i.test(a) ? Number(a.replace(/\D/g, '') || 0) : 999;
    const bi = /^ipelayer(\d*)$/i.test(b) ? Number(b.replace(/\D/g, '') || 0) : 999;
    return ai - bi;
  });

  const printSlotCountForSlide = (slideId) => {
    const id = String(slideId || '');
    const layerCount = Array.isArray(ipeLayers[id]) ? ipeLayers[id].length : 0;
    const areaValue = stampAreas[id];
    const areaCount = Array.isArray(areaValue)
      ? areaValue.length
      : (areaValue && typeof areaValue === 'object' ? Object.keys(areaValue).length : 0);
    let canvas = slideCanvases[id];
    if (!canvas && String(td.primaryImageId || '') === id) {
      canvas = td.fabricCanvas;
    }
    const objects = Array.isArray(canvas?.objects) ? canvas.objects : [];
    const canvasSlotCount = objects.filter((obj) => {
      if (!obj || obj.id === 'workspace') return false;
      return obj.linkData?.role === 'stampArea'
        || obj.stampRole === 'stampArea'
        || isIpeLayerName(obj.name);
    }).length;
    return Math.max(layerCount, areaCount, canvasSlotCount);
  };

  const toPage = (id, name) => ({
    id,
    name: name || id,
    slotCount: printSlotCountForSlide(id),
  });

  let pages = [];
  if (slideIds.length > 0) {
    pages = slideIds.map((id) => {
      const image = images.find((img) => String(img?.id) === String(id));
      return toPage(id, image?.name || id);
    });
  } else if (images.length > 0) {
    pages = images.map((img) => toPage(img.id, img.name || img.id));
  } else if (td.fabricCanvas) {
    const id = String(td.primaryImageId || 'primary');
    pages = [toPage(id, id)];
  }

  const billableSlideCount = pages.filter((page) => page.slotCount > 0).length;

  return {
    slideCount,
    billableSlideCount,
    slotCount: slotNames.length,
    slotNames,
    pages,
    usesIpeLayer: slotNames.some((n) => /^ipelayer(\d*)$/i.test(n)),
  };
}

export function summarizePodTemplateBatch(templates = []) {
  return templates.map((template) => ({
    template,
    meta: summarizePodTemplate(template),
  }));
}

/** 从套图模板 stampAreas 解析印花分组（与图片空间弹窗分配区一致） */
export function resolveStampGroupsFromTemplates(templates = []) {
  const allStampNames = new Set();
  templates.forEach((template) => {
    const stampAreas = template?.templateData?.stampAreas;
    if (!stampAreas) return;
    Object.values(stampAreas).forEach((stamps) => {
      if (Array.isArray(stamps)) {
        stamps.forEach((stamp) => {
          allStampNames.add(stamp.name || '未命名印花');
        });
      }
    });
  });
  return Array.from(allStampNames).map((name) => ({ name, originalName: name }));
}

/** 从指定 slide 列表收集印花位名称（stampAreas、画布 ipelayer/stamp、ipeLayers 元数据） */
function collectSlotNamesForSlideIds(template, slideIds = []) {
  const td = template?.templateData || {};
  const stampAreas = td.stampAreas || {};
  const ipeLayers = td.ipeLayers || {};
  const slideCanvases = td.slideCanvases || {};
  const ordered = [];
  const seen = new Set();

  const addName = (name) => {
    const normalized = String(name || '').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    ordered.push(normalized);
  };

  slideIds.forEach((slideId) => {
    const stamps = stampAreas[slideId];
    if (Array.isArray(stamps)) {
      stamps.forEach((stamp) => addName(stamp?.name));
    }

    const canvas = slideCanvases[slideId];
    if (canvas?.objects) {
      canvas.objects.forEach((obj) => {
        if (obj?.id === 'workspace') return;
        const isStamp = obj.linkData?.role === 'stampArea' || obj.stampRole === 'stampArea';
        const isIpe = isIpeLayerName(obj.name);
        if (isStamp) {
          addName(obj.linkData?.stampName || obj.stampName || obj.name);
        } else if (isIpe) {
          addName(obj.linkData?.stampName || normalizeIpeLayerName(obj.name));
        }
      });
    }

    const layers = ipeLayers[slideId];
    if (Array.isArray(layers)) {
      layers.forEach((layer) => addName(layer?.name));
    }
  });

  return ordered;
}

/**
 * 主图 + 细节图层级模板：按每个 SKC（主图及其细节）统计印花位，
 * 避免全局去重后只剩 1 个 ipelayer 导致「每张印花单独成组」。
 */
export function resolveStampSlotNamesPerSkc(template) {
  if (!template) return [];
  const td = template.templateData || {};
  const images = (Array.isArray(td.images) ? td.images : []).map((img) => (
    enrichSlideHierarchyFields(img)
  ));
  const hierarchyGroups = groupSlidesByMain(images);
  const fromGlobalStamps = resolveStampGroupsFromTemplates([template]).map((g) => g.name);

  if (hierarchyGroups.length > 0) {
    let best = [];
    hierarchyGroups.forEach(({ main, details }) => {
      const slideIds = [main?.id, ...(details || []).map((d) => d.id)].filter(Boolean);
      const names = collectSlotNamesForSlideIds(template, slideIds);
      if (names.length > best.length) best = names;
    });
    if (best.length >= fromGlobalStamps.length && best.length > 0) return best;
  }

  if (fromGlobalStamps.length > 0) return fromGlobalStamps;
  return summarizePodTemplate(template).slotNames;
}

/** 合并模板中的印花位名称（stampAreas 优先，否则 ipelayer） */
export function resolveStampSlotNames(templates = []) {
  if (templates.length === 1) {
    const perSkc = resolveStampSlotNamesPerSkc(templates[0]);
    if (perSkc.length > 0) return perSkc;
  }
  const fromGroups = resolveStampGroupsFromTemplates(templates);
  if (fromGroups.length > 0) return fromGroups.map((g) => g.name);
  const summaries = summarizePodTemplateBatch(templates);
  const set = new Set();
  summaries.forEach(({ meta }) => {
    meta.slotNames.forEach((n) => set.add(n));
  });
  return [...set];
}

/** 按印花位顺序将素材分组：第 i 张素材 → slotNames[i % slotCount] */
export function groupStampRefsBySlotName(templates = [], stampRefs = []) {
  const slotNames = resolveStampSlotNames(templates);
  if (slotNames.length === 0) {
    return stampRefs.length > 0 ? [{ name: '印花素材', stamps: [...stampRefs] }] : [];
  }
  return slotNames.map((name, slotIndex) => ({
    name,
    stamps: stampRefs.filter((_, i) => i % slotNames.length === slotIndex),
  }));
}

/** 按印花区域与素材顺序构建主图创建 goodsForms */
export function buildPodBatchGoodsForms(templates = [], stampRefs = [], options = {}) {
  const { printUrlOverrides = null, imageSpaceImages = [] } = options;
  if (!templates.length || !stampRefs.length) return [];

  const positionArray = stampRefs.map((ref) => String(ref.id));

  const selectionRefMap = new Map();
  if (printUrlOverrides && typeof printUrlOverrides === 'object') {
    Object.entries(printUrlOverrides).forEach(([id, url]) => {
      const u = url && String(url).trim();
      if (!id || !u) return;
      try {
        selectionRefMap.set(String(id), toPrintSlotRef({ id: String(id), url: u }));
      } catch (_) {
        selectionRefMap.set(String(id), { id: String(id), url: u, role: 'print' });
      }
    });
  }
  stampRefs.forEach((ref) => {
    const id = String(ref.id);
    if (selectionRefMap.has(id)) return;
    const url = ref.apiUrl || ref.previewUrl || ref.url || '';
    if (!url) {
      selectionRefMap.set(id, id);
      return;
    }
    try {
      selectionRefMap.set(id, toPrintSlotRef({
        id,
        url: String(url).trim(),
        name: ref.name,
        assetId: ref.assetId,
        role: ref.role || 'print',
      }));
    } catch (_) {
      selectionRefMap.set(id, { id, url: String(url).trim(), role: 'print', name: ref.name });
    }
  });
  imageSpaceImages.forEach((img) => {
    if (!img?.id) return;
    const key = String(img.id);
    if (selectionRefMap.has(key)) return;
    const refUrl = img.url || img.dataUrl || img.src || '';
    if (!refUrl) {
      selectionRefMap.set(key, key);
      return;
    }
    // 图片空间条目进入印花位时按 print 资产落槽（用户显式选作印花素材）
    try {
      selectionRefMap.set(key, toPrintSlotRef({ id: key, url: refUrl, name: img.name }));
    } catch (_) {
      selectionRefMap.set(key, { id: key, url: refUrl, role: 'print' });
    }
  });

  return templates.map((template) => {
    const templateSlots = resolveStampSlotNamesPerSkc(template);
    const batchSlots = resolveStampSlotNames(templates);
    const slotNames = templateSlots.length > 0
      ? templateSlots
      : (batchSlots.length > 0 ? batchSlots : ['印花1']);
    const slotCount = Math.max(1, slotNames.length);
    const groupCount = Math.max(1, Math.ceil(positionArray.length / slotCount));

    const imageGroups = [];
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
      const group = {};
      const groupStart = groupIndex * slotCount;
      slotNames.forEach((slotName, slotIndex) => {
        let positionIndex = groupStart + slotIndex;
        // 本组素材不足时复用本组首张（单印花灌满全部贴合位）
        if (positionIndex >= positionArray.length) {
          positionIndex = groupStart < positionArray.length
            ? groupStart
            : positionArray.length - 1;
        }
        const rawImageId = positionArray[positionIndex];
        if (!rawImageId) return;
        group[slotName] = selectionRefMap.get(String(rawImageId)) || String(rawImageId);
      });
      if (Object.keys(group).length > 0) imageGroups.push(group);
    }

    return {
      templateId: template.id,
      templateName: template.name || `模版 ${template.id}`,
      templateData: template.templateData,
      imageGroups,
      compositeResults: [],
    };
  }).filter((form) => (form.imageGroups || []).length > 0);
}

/** 计入预估费用的页：有印花区域或贴合位。无印花位的页只透传原图，不扣费。 */
export function countChargeableTemplateSlides(meta = {}) {
  const pages = Array.isArray(meta?.pages) ? meta.pages : [];
  if (pages.length > 0) {
    return pages.filter((page) => Number(page?.slotCount) > 0).length;
  }
  if (meta?.billableSlideCount != null && meta.billableSlideCount !== '') {
    const counted = Number(meta.billableSlideCount);
    if (Number.isFinite(counted)) return Math.max(0, counted);
  }
  return Math.max(0, Number(meta?.slideCount) || 0);
}

export function estimatePodBatchOutput(templateSummaries, stampCount, slotCount = 0) {
  const templates = templateSummaries.length;
  const stampImageCount = Math.max(0, stampCount);
  const slots = Math.max(1, slotCount > 0 ? slotCount : 1);
  const stampGroupCount = stampImageCount > 0
    ? Math.ceil(stampImageCount / slots)
    : 0;
  const mainImageCount = templateSummaries.reduce(
    (sum, { meta }) => sum + countChargeableTemplateSlides(meta),
    0,
  );
  const totalImages = stampGroupCount * mainImageCount;
  return {
    templates,
    stampImageCount,
    stampGroupCount,
    mainImageCount,
    totalImages,
  };
}
